import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Mail, ArrowLeft, KeyRound, CheckCircle2 } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [sent, setSent] = useState(false);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return toast.error("Please enter your email");
        setBusy(true);
        try {
            await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
            setSent(true);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Something went wrong — please try again");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-[70vh] flex items-center justify-center bg-stone-50 px-4 py-8" data-testid="forgot-password-page">
            <Toaster position="top-center" />
            <div className="w-full max-w-md bg-white border border-stone-200 p-8 space-y-5">
                <Link to="/login" className="text-xs uppercase tracking-widest text-sky-700 inline-flex items-center hover:underline" data-testid="back-to-login">
                    <ArrowLeft className="w-3 h-3 mr-1" />Back to sign in
                </Link>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 grid place-items-center bg-sky-700 text-stone-50">
                        <KeyRound className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="label-eyebrow text-sky-700">Account recovery</div>
                        <h1 className="font-serif text-2xl text-stone-900">Forgot password</h1>
                    </div>
                </div>

                {sent ? (
                    <div className="space-y-3" data-testid="forgot-sent">
                        <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 px-4 py-3">
                            <CheckCircle2 className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                            <div>
                                <div className="font-medium text-emerald-900">Check your inbox</div>
                                <p className="text-sm text-emerald-800 mt-1">
                                    If <strong className="font-mono">{email}</strong> is on file, we just emailed a reset link. It's valid for one hour.
                                </p>
                            </div>
                        </div>
                        <p className="text-xs text-stone-500">
                            Didn't get it? Check spam, or wait a minute and{" "}
                            <button onClick={() => setSent(false)} className="text-sky-700 hover:underline">try again</button>.
                        </p>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-4">
                        <p className="text-sm text-stone-600">
                            Enter the email you signed up with. We'll email a one-hour reset link.
                        </p>
                        <div>
                            <Label className="text-xs uppercase tracking-widest text-stone-500">Email</Label>
                            <div className="relative mt-1">
                                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                                <Input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    autoComplete="email"
                                    required
                                    className="rounded-none border-stone-300 pl-9"
                                    data-testid="forgot-email"
                                />
                            </div>
                        </div>
                        <Button type="submit" disabled={busy} className="w-full rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="forgot-submit">
                            {busy ? "Sending…" : "Send reset link"}
                        </Button>
                    </form>
                )}

                <div className="text-[11px] text-stone-500 border-t border-stone-200 pt-3">
                    Remembered your password? <Link to="/login" className="text-sky-700 hover:underline">Sign in</Link>
                </div>
            </div>
        </div>
    );
}
